import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTemplate } from '../components/templates/useTemplate';
import { InlineRename } from '../components/templates/InlineRename';
import { RowMenu } from '../components/templates/RowMenu';
import { ConfirmModal } from '../components/templates/ConfirmModal';
import { AutosaveIndicator } from '../components/templates/AutosaveIndicator';
import { SortableList } from '../components/templates/SortableList';
import { AttributeRow } from '../components/templates/AttributeRow';
import { CommentRow } from '../components/templates/CommentRow';
import { GripIcon, Icon } from '../components/templates/icons';
import {
  templatesApi,
  type TemplateComponent,
  type TemplateCommentKind,
  type TemplateComponentComment,
  type TemplateSection,
} from '../api/templates';

const SOP_MAX_CHARS = 10000;
const SOP_AUTOSAVE_DEBOUNCE_MS = 800;

type Confirm =
  | { kind: 'delete-component'; component: TemplateComponent }
  | { kind: 'restore-section' }
  | null;

export default function TemplateSectionPage() {
  const { templateId, sectionId = '' } = useParams<{ templateId: string; sectionId: string }>();
  // `templateId` is required by the route — pass it through so we load the
  // correct template (not just the user's default). Previously the page
  // silently fell back to the default template, which broke every time a
  // user opened a section inside an imported/duplicated template.
  const { template, loading, error, saveState, run, reload } = useTemplate(templateId);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);

  const section = useMemo<TemplateSection | undefined>(
    () => template?.sections.find((s) => s.id === sectionId),
    [template, sectionId],
  );

  if (loading && !template) {
    return <div className="ts-title-sub" style={{ padding: 24 }}>Loading section…</div>;
  }
  if (error && !template) {
    return (
      <div style={{ padding: 24 }}>
        <p className="ts-title-sub" style={{ color: 'var(--safety-fg)' }}>{error}</p>
        <button type="button" className="ts-btn ts-btn--secondary" onClick={reload}>Retry</button>
      </div>
    );
  }
  if (!template || !section) {
    return (
      <div style={{ padding: 24 }}>
        <p className="ts-title-sub">Section not found.</p>
        <Link to="/templates" className="ts-btn ts-btn--secondary">Back to template</Link>
      </div>
    );
  }

  const totalAttrs = section.components.reduce((n, c) => n + c.attributes.length, 0);
  const totalComments = section.components.reduce((n, c) => n + c.comments.length, 0);

  const handleRename = (name: string) => {
    run(() => templatesApi.updateSection(section.id, { name }));
  };
  const handleReorderComponents = (ids: string[]) => {
    run(
      () => templatesApi.reorderComponents(section.id, ids),
      (prev) => ({
        ...prev,
        sections: prev.sections.map((s) => {
          if (s.id !== section.id) return s;
          const byId = new Map(s.components.map((c) => [c.id, c]));
          const reordered = ids
            .map((id, i) => {
              const c = byId.get(id);
              return c ? { ...c, orderIndex: i } : null;
            })
            .filter((c): c is NonNullable<typeof c> => c !== null);
          const untouched = s.components.filter((c) => !ids.includes(c.id));
          return { ...s, components: [...reordered, ...untouched] };
        }),
      }),
    );
  };

  const handleReorderAttributes = (componentId: string, ids: string[]) => {
    run(
      () => templatesApi.reorderAttributes(componentId, ids),
      (prev) => ({
        ...prev,
        sections: prev.sections.map((s) => {
          if (s.id !== section.id) return s;
          return {
            ...s,
            components: s.components.map((c) => {
              if (c.id !== componentId) return c;
              const byId = new Map(c.attributes.map((a) => [a.id, a]));
              const reordered = ids
                .map((id, i) => {
                  const a = byId.get(id);
                  return a ? { ...a, orderIndex: i } : null;
                })
                .filter((a): a is NonNullable<typeof a> => a !== null);
              const untouched = c.attributes.filter((a) => !ids.includes(a.id));
              return { ...c, attributes: [...reordered, ...untouched] };
            }),
          };
        }),
      }),
    );
  };

  /**
   * Reordering comments is scoped per-kind at the UI level (notes render
   * separately from limitations), but the backend stores a single ordered
   * list of comments per component. We merge the incoming partial order
   * with untouched rows from the other kind so their server state stays
   * consistent with what the UI is showing.
   */
  const handleReorderComments = (componentId: string, kind: TemplateCommentKind, ids: string[]) => {
    run(
      () => {
        // Build the full ordered id list for the backend (kind-local order
        // first, then untouched of the other kind).
        const comp = section.components.find((c) => c.id === componentId);
        const otherIds = comp ? comp.comments.filter((cm) => cm.kind !== kind).map((cm) => cm.id) : [];
        return templatesApi.reorderComments(componentId, [...ids, ...otherIds]);
      },
      (prev) => ({
        ...prev,
        sections: prev.sections.map((s) => {
          if (s.id !== section.id) return s;
          return {
            ...s,
            components: s.components.map((c) => {
              if (c.id !== componentId) return c;
              const byId = new Map(c.comments.map((cm) => [cm.id, cm]));
              const reordered = ids
                .map((id, i) => {
                  const cm = byId.get(id);
                  return cm ? { ...cm, orderIndex: i } : null;
                })
                .filter((cm): cm is NonNullable<typeof cm> => cm !== null);
              const others = c.comments.filter((cm) => cm.kind !== kind);
              return { ...c, comments: [...reordered, ...others] };
            }),
          };
        }),
      }),
    );
  };

  const handleCreateComponent = () => {
    run(() => templatesApi.createComponent(section.id, 'New component'));
  };

  const closeConfirm = () => { if (!busy) setConfirm(null); };
  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'delete-component') {
        await run(() => templatesApi.deleteComponent(confirm.component.id));
      } else if (confirm.kind === 'restore-section') {
        await run(() => templatesApi.restoreSection(section.id));
      }
      setConfirm(null);
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="ts-breadcrumb">
        <Link to={`/templates/${template.id}`}>{template.name}</Link>
        <span className="ts-breadcrumb-sep">/</span>
        <span>{section.name}</span>
      </div>

      <div className="ts-title-row">
        <div>
          <h1 className="ts-title">
            <InlineRename
              value={section.name}
              onCommit={handleRename}
              font="display"
              size={38}
              ariaLabel={`Rename section ${section.name}`}
            />
          </h1>
          <div className="ts-title-sub">
            {section.isCustomized && <span className="ts-customized" style={{ marginRight: 8 }}>Customized</span>}
            {section.components.length} components · {totalAttrs} facts · {totalComments} comments
          </div>
        </div>
        <div className="ts-head-actions">
          <AutosaveIndicator state={saveState} onRetry={reload} />
          <button
            type="button"
            className="ts-btn ts-btn--destructive-ghost ts-btn--sm"
            onClick={() => setConfirm({ kind: 'restore-section' })}
          >
            Restore section
          </button>
        </div>
      </div>

      <SopBlock
        key={section.id}
        section={section}
        onSave={(body) => run(() => templatesApi.updateSopBody(section.id, body))}
        onRestoreDefault={() => run(() => templatesApi.restoreSopDefault(section.id))}
      />

      <div className="ts-card">
        <div className="ts-card-head">
          <span className="ts-card-head-label">Components</span>
          <span className="ts-card-head-meta">{section.components.length} · drag to reorder · click to expand</span>
        </div>
        <SortableList
          groupKey={`components-${section.id}`}
          items={section.components}
          onReorder={handleReorderComponents}
          renderItem={(comp, { dragHandleProps, state }) => (
            <ComponentRow
              component={comp}
              expanded={expanded === comp.id}
              onToggle={() => setExpanded((id) => (id === comp.id ? null : comp.id))}
              onRename={(name) => run(() => templatesApi.updateComponent(comp.id, { name }))}
              onDelete={() => setConfirm({ kind: 'delete-component', component: comp })}
              onCreateAttribute={() => run(() => templatesApi.createAttribute(comp.id, { key: 'New attribute' }))}
              onUpdateAttribute={(attrId, patch) => run(() => templatesApi.updateAttribute(attrId, patch))}
              onDeleteAttribute={(attrId) => run(() => templatesApi.deleteAttribute(attrId))}
              onReorderAttributes={(ids) => handleReorderAttributes(comp.id, ids)}
              onCreateComment={(kind) =>
                run(() =>
                  templatesApi.createComment(comp.id, {
                    kind,
                    body: kind === 'note' ? 'New note' : 'New limitation',
                  }),
                )
              }
              onUpdateComment={(cmtId, patch) => run(() => templatesApi.updateComment(cmtId, patch))}
              onDeleteComment={(cmtId) => run(() => templatesApi.deleteComment(cmtId))}
              onReorderComments={(kind, ids) => handleReorderComments(comp.id, kind, ids)}
              dragHandleProps={dragHandleProps}
              dragState={state}
            />
          )}
        />
        <button
          type="button"
          className="ts-attr-add"
          style={{ borderTop: '1px solid var(--border-1)' }}
          onClick={handleCreateComponent}
        >
          <Icon name="plus" size={12} /> Add component
        </button>
      </div>

      {confirm?.kind === 'delete-component' && (
        <ConfirmModal
          title="Delete this component?"
          body={
            <>
              Delete <strong>&ldquo;{confirm.component.name}&rdquo;</strong>? This component and
              its <strong>{confirm.component.attributes.length} facts</strong> and{' '}
              <strong>{confirm.component.comments.length} comments</strong> will be removed.
            </>
          }
          confirmLabel="Delete component"
          busy={busy}
          onCancel={closeConfirm}
          onConfirm={runConfirm}
        />
      )}
      {confirm?.kind === 'restore-section' && (
        <ConfirmModal
          title={`Restore '${section.name}' to default?`}
          body={<>Restore the <strong>{section.name}</strong> section to the Flowspect default. Any customizations you&rsquo;ve made to this section will be lost.</>}
          confirmLabel="Restore section"
          busy={busy}
          onCancel={closeConfirm}
          onConfirm={runConfirm}
        />
      )}
    </>
  );
}

interface ComponentRowProps {
  component: TemplateComponent;
  expanded: boolean;
  onToggle: () => void;
  onRename: (name: string) => void;
  onDelete: () => void;
  onCreateAttribute: () => void;
  onUpdateAttribute: (attrId: string, patch: { key?: string; value?: string | null }) => void;
  onDeleteAttribute: (attrId: string) => void;
  onReorderAttributes: (ids: string[]) => void;
  onCreateComment: (kind: TemplateCommentKind) => void;
  onUpdateComment: (
    commentId: string,
    patch: { kind?: TemplateCommentKind; body?: string; order_index?: number },
  ) => void;
  onDeleteComment: (commentId: string) => void;
  onReorderComments: (kind: TemplateCommentKind, ids: string[]) => void;
  dragHandleProps: { draggable: true; onDragStart: (e: React.DragEvent) => void; onDragEnd: () => void };
  dragState: 'resting' | 'dragging' | 'drop-target' | 'ghost';
}

function ComponentRow({
  component,
  expanded,
  onToggle,
  onRename,
  onDelete,
  onCreateAttribute,
  onUpdateAttribute,
  onDeleteAttribute,
  onReorderAttributes,
  onCreateComment,
  onUpdateComment,
  onDeleteComment,
  onReorderComments,
  dragHandleProps,
  dragState,
}: ComponentRowProps) {
  const notes = component.comments.filter((c) => c.kind === 'note');
  const limitations = component.comments.filter((c) => c.kind === 'limitation');
  const cls = ['ts-row', 'ts-row--component'];
  if (expanded) cls.push('ts-row--on');
  if (dragState === 'dragging') cls.push('ts-row--dragging');
  if (dragState === 'drop-target') cls.push('ts-row--drop-target');

  return (
    <>
      <div className={cls.join(' ')} onClick={onToggle} role="button" tabIndex={0}>
        <span className="ts-grip" aria-label="Drag to reorder component" {...dragHandleProps}>
          <GripIcon size={14} />
        </span>
        <div className="ts-sec-name">
          <span className="ts-comp-name">
            <InlineRename value={component.name} onCommit={onRename} font="sans" size={13.5} ariaLabel={`Rename component ${component.name}`} />
          </span>
        </div>
        <span className="ts-comp-meta">
          {component.attributes.length} facts · {notes.length} notes · {limitations.length} limitations
        </span>
        <span className="ts-chev"><Icon name={expanded ? 'chevDown' : 'chev'} size={14} /></span>
        <div onClick={(e) => e.stopPropagation()}>
          <RowMenu
            items={[
              { label: 'Delete', icon: 'trash', danger: true, onClick: onDelete },
            ]}
          />
        </div>
      </div>
      {expanded && (
        <div className="ts-drawer" onClick={(e) => e.stopPropagation()}>
          {/* Facts (formerly "Attributes") — key:value pairs */}
          <div className="ts-cmt-heading">
            <span>Facts</span>
            <span className="ts-cmt-heading-count">
              {component.attributes.length === 1 ? '1 fact' : `${component.attributes.length} facts`}
            </span>
          </div>
          {component.attributes.length === 0 ? (
            <div className="ts-empty">
              <div className="ts-empty-title">No facts yet.</div>
              <div className="ts-empty-text">
                Facts are the key:value details you record per inspection — e.g., <em>Siding material: Cedar lap, painted</em>.
              </div>
              <div className="ts-empty-cta">
                <button type="button" className="ts-btn ts-btn--secondary ts-btn--sm" onClick={onCreateAttribute}>
                  <Icon name="plus" size={12} /> Add fact
                </button>
              </div>
            </div>
          ) : (
            <div className="ts-attr-list">
              <SortableList
                groupKey={`attrs-${component.id}`}
                items={component.attributes}
                onReorder={onReorderAttributes}
                renderItem={(attr, { dragHandleProps: dp, state }) => (
                  <AttributeRow
                    initialKey={attr.key}
                    initialValue={attr.value}
                    onCommit={(patch) => onUpdateAttribute(attr.id, patch)}
                    onDelete={() => onDeleteAttribute(attr.id)}
                    dragHandleProps={dp}
                    dragActive={state === 'dragging'}
                    dragOver={state === 'drop-target'}
                  />
                )}
              />
              <button type="button" className="ts-attr-add" onClick={onCreateAttribute}>
                <Icon name="plus" size={12} /> Add fact
              </button>
            </div>
          )}

          {/* Notes — prose observations that become narrative paragraphs in the report */}
          <CommentListSection
            componentId={component.id}
            kind="note"
            items={notes}
            onCreate={() => onCreateComment('note')}
            onUpdate={onUpdateComment}
            onDelete={onDeleteComment}
            onReorder={(ids) => onReorderComments('note', ids)}
          />

          {/* Limitations — scope exclusions that become "Areas Not Fully Inspected" in the report */}
          <CommentListSection
            componentId={component.id}
            kind="limitation"
            items={limitations}
            onCreate={() => onCreateComment('limitation')}
            onUpdate={onUpdateComment}
            onDelete={onDeleteComment}
            onReorder={(ids) => onReorderComments('limitation', ids)}
          />
        </div>
      )}
    </>
  );
}

interface CommentListSectionProps {
  componentId: string;
  kind: TemplateCommentKind;
  items: TemplateComponentComment[];
  onCreate: () => void;
  onUpdate: (
    commentId: string,
    patch: { kind?: TemplateCommentKind; body?: string; order_index?: number },
  ) => void;
  onDelete: (commentId: string) => void;
  onReorder: (ids: string[]) => void;
}

function CommentListSection({
  componentId,
  kind,
  items,
  onCreate,
  onUpdate,
  onDelete,
  onReorder,
}: CommentListSectionProps) {
  const label = kind === 'note' ? 'Notes' : 'Limitations';
  const singular = kind === 'note' ? 'note' : 'limitation';
  const emptyBody =
    kind === 'note'
      ? 'Notes are prose observations for the report — e.g., "HOA-controlled roof; contact HOA for concerns."'
      : 'Limitations describe scope exclusions — e.g., "Snow cover prevented visual inspection of the north slope."';

  return (
    <>
      <div className="ts-cmt-heading">
        <span>{label}</span>
        <span className="ts-cmt-heading-count">
          {items.length} {items.length === 1 ? singular : `${singular}s`}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="ts-empty">
          <div className="ts-empty-title">No {singular}s yet.</div>
          <div className="ts-empty-text">{emptyBody}</div>
          <div className="ts-empty-cta">
            <button
              type="button"
              className="ts-btn ts-btn--secondary ts-btn--sm"
              onClick={onCreate}
            >
              <Icon name="plus" size={12} /> Add {singular}
            </button>
          </div>
        </div>
      ) : (
        <div className="ts-cmt-list">
          <SortableList
            groupKey={`cmts-${kind}-${componentId}`}
            items={items}
            onReorder={onReorder}
            renderItem={(cmt, { dragHandleProps: dp, state }) => (
              <CommentRow
                kind={cmt.kind}
                initialBody={cmt.body}
                onCommit={(patch) => onUpdate(cmt.id, patch)}
                onDelete={() => onDelete(cmt.id)}
                dragHandleProps={dp}
                dragActive={state === 'dragging'}
                dragOver={state === 'drop-target'}
              />
            )}
          />
          <button
            type="button"
            className={
              'ts-cmt-add' + (kind === 'limitation' ? ' ts-cmt-add--limitation' : '')
            }
            onClick={onCreate}
          >
            <Icon name="plus" size={12} /> Add {singular}
          </button>
        </div>
      )}
    </>
  );
}

interface SopBlockProps {
  section: TemplateSection;
  onSave: (body: string | null) => Promise<unknown>;
  onRestoreDefault: () => Promise<unknown>;
}

/**
 * Per-section Standards of Practice editor. Sits between the Information
 * block and the Components list on every section page. The textarea
 * autosaves on a short debounce through the page-level `useTemplate.run`
 * helper, so the parent template state is the single source of truth and
 * page-level autosave + Restore section both stay in sync with the
 * textarea. Clearing the textarea stores NULL, which renders as
 * "no SoP for this section" in the report. When the body is empty the
 * inspector also gets a "Use Flowspect default" button that fills the
 * textarea from the matching seed entry (with the alias map for common
 * Spectora variants); the button surfaces an inline error if no seed
 * matches the section name.
 */
function SopBlock({ section, onSave, onRestoreDefault }: SopBlockProps) {
  const serverBody = section.sopBody ?? '';
  const [draft, setDraft] = useState(serverBody);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const lastSavedRef = useRef(serverBody);
  const debounceRef = useRef<number | null>(null);

  // Re-sync if the server version changes from outside (Restore section,
  // multi-tab edits, etc.). Comparing against lastSavedRef avoids stomping
  // an in-flight local edit when a save round-trip echoes the same value
  // we just sent.
  useEffect(() => {
    if (serverBody !== lastSavedRef.current) {
      setDraft(serverBody);
      lastSavedRef.current = serverBody;
    }
  }, [serverBody]);

  useEffect(() => () => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
  }, []);

  const overLimit = draft.length > SOP_MAX_CHARS;

  const scheduleSave = (next: string) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      debounceRef.current = null;
      if (next === lastSavedRef.current) return;
      if (next.length > SOP_MAX_CHARS) return;
      const trimmed = next.trim();
      const body = trimmed.length === 0 ? null : next;
      lastSavedRef.current = next;
      // onSave is wired to useTemplate.run upstream — it owns the
      // saving / saved / error indicator and refreshes the template
      // tree so any other consumer (e.g. ts-title-sub counts) stays
      // consistent with what we just wrote.
      await onSave(body);
    }, SOP_AUTOSAVE_DEBOUNCE_MS);
  };

  const handleChange = (value: string) => {
    setDraft(value);
    scheduleSave(value);
  };

  const handleRestoreDefault = async () => {
    setRestoring(true);
    setRestoreError(null);
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    try {
      await onRestoreDefault();
    } catch (e) {
      setRestoreError((e as Error).message ?? 'Could not load a default for this section.');
    } finally {
      setRestoring(false);
    }
  };

  const isEmpty = section.sopBody === null;

  return (
    <div className="ts-card" style={{ marginTop: 14, marginBottom: 14, padding: '10px 14px 14px' }}>
      <div
        className="ts-card-head"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, padding: 0, border: 'none', background: 'transparent' }}
      >
        <div>
          <span className="ts-card-head-label">Standards of Practice</span>
          <span className="ts-card-head-meta" style={{ marginLeft: 8 }}>
            {isEmpty
              ? 'Empty · no SoP for this section'
              : 'Snapshotted into every report'}
          </span>
        </div>
        {isEmpty && (
          <button
            type="button"
            className="ts-btn ts-btn--secondary ts-btn--sm"
            onClick={handleRestoreDefault}
            disabled={restoring}
          >
            {restoring ? 'Loading…' : 'Use Flowspect default'}
          </button>
        )}
      </div>
      <textarea
        id={`sop-body-${section.id}`}
        className="ts-sop-textarea"
        value={draft}
        placeholder={`Describe what's inspected and what's excluded for ${section.name}. Renders under each section header in the client report.`}
        onChange={(e) => handleChange(e.target.value)}
        spellCheck
        aria-label={`Standards of Practice for ${section.name}`}
        aria-invalid={overLimit || undefined}
      />
      <div className={`ts-sop-count${overLimit ? ' ts-sop-count--over' : ''}`}>
        <span>
          {overLimit
            ? `Over limit — remove ${draft.length - SOP_MAX_CHARS} character${draft.length - SOP_MAX_CHARS === 1 ? '' : 's'} to save.`
            : 'Starter text — review against your certifying body’s standards before sending. Edits apply to new reports; previously sent reports are unaffected.'}
        </span>
        <span>{draft.length.toLocaleString()} / {SOP_MAX_CHARS.toLocaleString()}</span>
      </div>
      {restoreError && (
        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--safety-fg)' }}>
          {restoreError}
        </div>
      )}
    </div>
  );
}
